// Public candle endpoint used by Vantage Web Trading. No account, cookies,
// token or MT5 terminal is needed. This is FixedVol40, not Headway VOL_80.
const API = 'https://appv2.nv.maikaihonua.com:18008/api/kline/query';
const MINUTE = 60000;
const PERIODS = Object.freeze({ M1: 1, M5: 5, M15: 15, M30: 30, H1: 60, H4: 240, D1: 1440 });
export const VANTAGE_REFRESH_MS = 5000;

export function decodeVantageCandles(value, period, now = Date.now()) {
  const data = value?.data;
  if (value?.code !== 0 || data?.symbol !== 'FixedVol40' || data?.baseSymbol !== 'FixedVol40' ||
      data?.period !== period || !Number.isInteger(data?.timeZone) || Math.abs(data.timeZone) > 14 ||
      !Array.isArray(data?.klines) || !data.klines.length) throw new Error('Data FixedVol40 Vantage tidak tersedia.');
  const offset = data.timeZone * 60;
  const unique = new Map();
  for (const row of data.klines) {
    // Provider timestamps encode broker wall time, including D1 midnight.
    // Convert once to actual UTC; retain the broker offset for H4/D1 buckets.
    const time = (row.timestamp - offset * 60) * 1000;
    if (!Number.isSafeInteger(row.timestamp) || row.timestamp % (period * 60) !== 0 ||
        time <= 0 || time > now || ![row.open, row.high, row.low, row.close].every(p => Number.isFinite(p) && p > 0) ||
        row.low > Math.min(row.open, row.close) || row.high < Math.max(row.open, row.close) ||
        !Number.isFinite(row.volume) || row.volume < 0) throw new Error('Data candle FixedVol40 tidak sah.');
    const bar = { time, open: row.open, high: row.high, low: row.low, close: row.close, volume: row.volume };
    const previous = unique.get(time);
    if (!previous || bar.volume >= previous.volume) unique.set(time, bar);
  }
  return { offset, candles: [...unique.values()].sort((a, b) => a.time - b.time) };
}

function bucket(time, period, offset) {
  const shift = offset * MINUTE;
  return Math.floor((time + shift) / period) * period - shift;
}

// Build a bar only when every constituent interval from its opening is known.
// A gap never becomes an invented open/high/low or a flat placeholder candle.
export function aggregateCovered(parts, period, offset) {
  const groups = new Map();
  for (const part of parts) {
    const start = bucket(part.time, period, offset);
    const group = groups.get(start) || [];
    group.push(part); groups.set(start, group);
  }
  const finalEnd = parts.at(-1)?.end || 0;
  const output = [];
  for (const [time, group] of groups) {
    let cursor = time;
    for (const part of group) {
      if (part.time !== cursor || part.end <= part.time || part.end > time + period) break;
      cursor = part.end;
    }
    if (cursor !== Math.min(time + period, finalEnd)) continue;
    output.push({ time, end: cursor, open: group[0].open, close: group.at(-1).close,
      high: Math.max(...group.map(p => p.high)), low: Math.min(...group.map(p => p.low)),
      volume: group.reduce((sum, p) => sum + p.volume, 0) });
  }
  return output;
}

export function combineVantageCandles(history, minutes, hours, period, now) {
  if (history.offset !== minutes.offset || (hours && history.offset !== hours.offset)) throw new Error('Sesi masa FixedVol40 tidak sepadan.');
  const offset = history.offset;
  const minuteParts = minutes.candles.filter(bar => bar.time + MINUTE <= now).map(bar => ({ ...bar, end: bar.time + MINUTE }));
  let parts = minuteParts;
  if (period > 60) {
    const byHour = new Map(hours.candles.filter(bar => bar.time + 60 * MINUTE <= now)
      .map(bar => [bar.time, { ...bar, end: bar.time + 60 * MINUTE }]));
    for (const bar of aggregateCovered(minuteParts, 60 * MINUTE, offset)) {
      if (!byHour.has(bar.time) || bar.end >= byHour.get(bar.time).end) byHour.set(bar.time, bar);
    }
    parts = [...byHour.values()].sort((a, b) => a.time - b.time);
  }
  const byTime = new Map(history.candles.map(bar => [bar.time, { ...bar, end: Math.min(now, bar.time + period * MINUTE) }]));
  const lastHistoryTime = history.candles.at(-1).time;
  for (const bar of aggregateCovered(parts, period * MINUTE, offset)) {
    if (bar.time >= lastHistoryTime && (!byTime.has(bar.time) || bar.end >= byTime.get(bar.time).end)) byTime.set(bar.time, bar);
  }
  const combined = [...byTime.values()].sort((a, b) => a.time - b.time);
  return { candles: combined.map(({ end, ...bar }) => bar), latestCandleTime: combined.at(-1).end };
}

export function createVantageLoader() {
  // Small, session-only cache of public history. M1 refreshes every 5 seconds;
  // unchanged higher-timeframe archives do not need downloading at that rate.
  const cache = new Map();
  return async function load(symbol, tf, { fetchImpl = fetch, signal, now = Date.now() } = {}) {
    if (symbol !== 'VOL40' || !Object.hasOwn(PERIODS, tf)) throw new Error('Unsupported Vantage market request.');
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    const requestSignal = signal ? AbortSignal.any([signal, AbortSignal.timeout(10000)]) : AbortSignal.timeout(10000);
    const period = PERIODS[tf];
    const count = period >= 240 ? 300 : 600;
    async function bars(frame, size) {
      const key = `${frame}:${size}`;
      const saved = cache.get(key);
      if (saved && saved.fetchImpl === fetchImpl && now >= saved.at && now - saved.at < (frame === 1 ? 3000 : 30000)) return saved.value;
      const response = await fetchImpl(API, { method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'omit', cache: 'no-store', signal: requestSignal,
        body: JSON.stringify({ period: frame, symbol: 'FixedVol40', size, to: '' }) });
      if (!response.ok) throw new Error('Sambungan FixedVol40 Vantage terganggu. Cuba semula.');
      const value = decodeVantageCandles(await response.json(), frame, now);
      if (requestSignal.aborted) throw new DOMException('Aborted', 'AbortError');
      // Different provider replicas can briefly return an older last minute.
      // Keep already received complete bars; freshness still uses candle time.
      if (saved && saved.fetchImpl === fetchImpl && saved.value.offset === value.offset && now >= saved.at) {
        const merged = new Map(saved.value.candles.map(bar => [bar.time, bar]));
        for (const bar of value.candles) {
          const previous = merged.get(bar.time);
          if (!previous || bar.volume >= previous.volume) merged.set(bar.time, bar);
        }
        value.candles = [...merged.values()].sort((a, b) => a.time - b.time).slice(-size);
      }
      cache.set(key, { at: now, fetchImpl, value });
      return value;
    }
    const [history, recent, hours] = await Promise.all([
      bars(period, count), period === 1 ? Promise.resolve(null) : bars(1, 65),
      period > 60 ? bars(60, 25) : Promise.resolve(null),
    ]);
    if (requestSignal.aborted) throw new DOMException('Aborted', 'AbortError');
    if (history.candles.length < 2) throw new Error('Sejarah FixedVol40 belum mencukupi.');
    const result = combineVantageCandles(history, recent || history, hours, period, now);
    return { symbol, source: 'Vantage', priceType: 'candle', sessionOffsetMinutes: history.offset,
      timeframe: tf, fetchedAt: new Date(now).toISOString(), ...result, candles: result.candles.slice(-count) };
  };
}

export const loadVantageCandles = createVantageLoader();
